import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Brain, Copy, Check, Zap, Clock,
  Users, Sun, CloudRain, ThermometerSun, Star,
  TrendingUp, ChevronDown, Target,
} from 'lucide-react';

// ─── AI Cheat Codes ─────────────────────────────────────────────────
// Context-aware scripts that update based on: time of day, weather,
// table size, what's selling, what needs to move, and what works.
// The server's unfair advantage.

interface CheatCode {
  id: string;
  category: 'opening' | 'upsell' | 'drink' | 'dessert' | 'recovery' | 'bounty';
  title: string;
  context: string;
  script: string;
  why: string;
  successRate: number;
  tags: string[];
}

const CHEAT_CODES: CheatCode[] = [
  {
    id: '1', category: 'opening', title: 'The Warm Read',
    context: 'Group of 4, seated at 6:45 PM, one person is looking around (probably been here before)',
    script: '"Welcome back! — or is this your first time? Either way, you picked a great night. Can I start you with drinks while you look at the menu?"',
    why: '"Welcome back" makes regulars feel recognized. "First time?" makes new guests feel special. You win either way.',
    successRate: 82, tags: ['evening', 'group'],
  },
  {
    id: '2', category: 'upsell', title: 'The Wagyu Close',
    context: 'Table is looking at entrees, hasn\'t decided yet. Works best for tables of 2-4.',
    script: '"If you want the best thing on our menu — honestly, the Wagyu platter. I had it last week and I\'m still thinking about it. It\'s enough to share if you want to try it."',
    why: 'Personal recommendation ("I had it") converts 3x better than generic suggestions. "Share" lowers the commitment.',
    successRate: 68, tags: ['dinner', 'premium'],
  },
  {
    id: '3', category: 'drink', title: 'The No-Brainer Pour',
    context: 'Table just sat down, warm evening, casual vibe',
    script: '"Can I get you started with our house margarita? It\'s $9 and honestly it\'s dangerous — people always end up ordering a second one."',
    why: 'Naming a specific drink + a low price + social proof ("people always") removes decision fatigue. Warm weather = 40% more drink sales.',
    successRate: 71, tags: ['warm weather', 'casual'],
  },
  {
    id: '4', category: 'bounty', title: 'Halibut Hustle Script',
    context: 'ACTIVE BOUNTY: Kitchen needs to move halibut. $10 per sale.',
    script: '"Quick heads up — the chef just told me the halibut is the freshest we\'ve had in weeks. If you like fish at all, tonight\'s the night. It\'s pan-seared with a lemon butter that\'s kind of ridiculous."',
    why: 'Urgency + insider knowledge + sensory description. This script has a 58% conversion rate on the bounty.',
    successRate: 58, tags: ['bounty', 'kitchen push'],
  },
  {
    id: '5', category: 'dessert', title: 'The Table Share',
    context: 'Table is winding down, asking for the check. Any table size.',
    script: '"Before I grab the check — have you guys had our Dubai Chocolate? It\'s this pistachio-filled thing and it\'s perfect to share. I\'ll bring one for the table?"',
    why: '"For the table" removes individual commitment. Asking it as they\'re leaving catches the "why not" impulse.',
    successRate: 43, tags: ['end of meal', 'any size'],
  },
  {
    id: '6', category: 'recovery', title: 'The Graceful Pivot',
    context: 'Guest declines your upsell suggestion',
    script: '"Totally get it — the [regular item] is excellent too, great choice. Want me to add a side of the garlic bread? It goes really well with that."',
    why: 'Validate their choice first ("great choice"), then offer a smaller add-on. Recovering a small sale is better than no sale.',
    successRate: 31, tags: ['after decline', 'save'],
  },
  {
    id: '7', category: 'upsell', title: 'The Comparison Play',
    context: 'Guest is deciding between a regular and premium version of an item',
    script: '"Most people go with the regular, but the ones who try the premium always come back for it. It\'s only $8 more and you get the house-made sauce and extra meat."',
    why: 'Social proof + FOMO + specific price difference. Framing the upgrade as a small add-on rather than a total price.',
    successRate: 54, tags: ['upgrade', 'decision point'],
  },
  {
    id: '8', category: 'drink', title: 'The Wine Pairing Play',
    context: 'Table has ordered entrees. Revenue share bounty active on wine bottles.',
    script: '"Great choices. That ribeye pairs amazing with our Cabernet — want me to bring a bottle for the table? It\'s $60 and it\'ll change the whole meal."',
    why: 'Pairing specific wine + specific food gives you authority. "For the table" distributes cost mentally. Active bounty = $3 extra for you.',
    successRate: 38, tags: ['bounty', 'wine', 'pairing'],
  },
];

const categoryConfig: Record<string, { label: string; color: string; bg: string; icon: React.ComponentType<{ className?: string }> }> = {
  opening: { label: 'Opening', color: 'text-blue-400', bg: 'bg-blue-500/10', icon: Users },
  upsell: { label: 'Upsell', color: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: TrendingUp },
  drink: { label: 'Drink', color: 'text-purple-400', bg: 'bg-purple-500/10', icon: Star },
  dessert: { label: 'Dessert', color: 'text-pink-400', bg: 'bg-pink-500/10', icon: Star },
  recovery: { label: 'Recovery', color: 'text-amber-400', bg: 'bg-amber-500/10', icon: Target },
  bounty: { label: 'Bounty', color: 'text-orange-400', bg: 'bg-orange-500/10', icon: Zap },
};

const CheatCodes: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<string>('all');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text.replace(/"/g, ''));
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const filtered = filter === 'all' ? CHEAT_CODES : CHEAT_CODES.filter(c => c.category === filter);

  // Context signals
  const signals = [
    { icon: Clock, label: '6:45 PM', detail: 'Dinner rush' },
    { icon: ThermometerSun, label: '74°F', detail: 'Push cold drinks' },
    { icon: Users, label: '14 covers', detail: 'in your section' },
    { icon: Zap, label: '2 bounties', detail: 'active now' },
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-900/30 to-purple-900/30 border-b border-gray-900">
        <div className="max-w-lg mx-auto px-4 py-5">
          <button onClick={() => navigate('/restaurant/mockups')} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-300 text-sm mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Hub
          </button>
          <div className="flex items-center gap-3 mb-1">
            <Brain className="w-7 h-7 text-indigo-400" />
            <h1 className="text-xl font-bold">AI Cheat Codes</h1>
          </div>
          <p className="text-sm text-gray-500">Context-aware scripts. Updated live based on what's happening right now.</p>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {/* Context signals */}
        <div className="grid grid-cols-4 gap-2">
          {signals.map((s, i) => (
            <div key={i} className="bg-gray-900 border border-gray-800 rounded-lg p-2.5 text-center">
              <s.icon className="w-3.5 h-3.5 text-indigo-400 mx-auto mb-1" />
              <div className="text-xs font-medium text-white">{s.label}</div>
              <div className="text-[10px] text-gray-600">{s.detail}</div>
            </div>
          ))}
        </div>

        {/* Explanation */}
        <div className="bg-indigo-500/5 border border-indigo-500/15 rounded-xl p-4">
          <p className="text-xs text-gray-400">
            These scripts are generated from <span className="text-indigo-300 font-medium">what actually works at your restaurant</span> —
            built from real sales data, time of day, weather, and what's converting tonight.
            The bounty scripts are tied to active challenges. Use them word-for-word or adapt them.
          </p>
        </div>

        {/* Filter */}
        <div className="flex gap-1 overflow-x-auto pb-1">
          {[
            { key: 'all', label: 'All' },
            ...Object.entries(categoryConfig).map(([key, cfg]) => ({ key, label: cfg.label })),
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                filter === f.key ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30' : 'text-gray-500 border border-transparent hover:text-gray-300'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Scripts */}
        <div className="space-y-2">
          {filtered.map((code) => {
            const cc = categoryConfig[code.category];
            const CatIcon = cc.icon;
            const isExpanded = expandedId === code.id;
            return (
              <div key={code.id} className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                <button
                  onClick={() => setExpandedId(isExpanded ? null : code.id)}
                  className="w-full text-left p-4 hover:bg-gray-800/30 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg ${cc.bg} flex items-center justify-center flex-shrink-0`}>
                      <CatIcon className={`w-4 h-4 ${cc.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-semibold text-white">{code.title}</span>
                        <span className={`text-[10px] font-medium ${cc.color}`}>{cc.label}</span>
                        {code.category === 'bounty' && (
                          <span className="text-[10px] text-orange-400 animate-pulse font-bold">LIVE</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">{code.context}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-bold text-emerald-400">{code.successRate}%</div>
                      <div className="text-[10px] text-gray-600">success</div>
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-gray-800 p-4 space-y-3">
                    <div>
                      <div className="text-[10px] text-gray-600 uppercase tracking-wider mb-1">When to use</div>
                      <p className="text-xs text-gray-400">{code.context}</p>
                    </div>

                    {/* The script */}
                    <div className="bg-indigo-500/5 border border-indigo-500/15 rounded-lg p-3 relative">
                      <p className="text-sm text-indigo-200 italic leading-relaxed pr-8">{code.script}</p>
                      <button
                        onClick={() => handleCopy(code.script, code.id)}
                        className="absolute top-2 right-2 p-1.5 text-indigo-400 hover:text-indigo-300"
                      >
                        {copiedId === code.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>

                    <div>
                      <div className="text-[10px] text-gray-600 uppercase tracking-wider mb-1">Why it works</div>
                      <p className="text-xs text-gray-400">{code.why}</p>
                    </div>

                    <div className="flex items-center gap-3 text-xs text-gray-600">
                      {code.tags.map((tag, i) => (
                        <span key={i} className="bg-gray-800 px-2 py-0.5 rounded-full">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CheatCodes;
